import type { TabInfo } from '@br/protocol';
import { cx } from '../components/ui';
import { shortUrl } from '../lib/format';

export interface TabStripProps {
  tabs: TabInfo[];
  /** Tab that is active in the remote Chrome window */
  activeTabId?: string;
  /** Tab this participant is looking at */
  viewingTabId?: string;
  canClose: boolean;
  canCreate: boolean;
  onSelect: (tabId: string) => void;
  onClose: (tabId: string) => void;
  onCreate: () => void;
}

export function TabStrip({
  tabs,
  activeTabId,
  viewingTabId,
  canClose,
  canCreate,
  onSelect,
  onClose,
  onCreate,
}: TabStripProps) {
  return (
    <div className="flex items-stretch gap-1 overflow-x-auto border-b border-line bg-surface px-2 pt-1">
      {tabs.length === 0 && (
        <span className="px-2 py-1.5 text-xs text-ink-muted">열린 탭이 없습니다</span>
      )}
      {tabs.map((t) => {
        const viewing = t.tabId === viewingTabId;
        const active = t.tabId === activeTabId;
        return (
          <div
            key={t.tabId}
            role="tab"
            aria-selected={viewing}
            tabIndex={0}
            onClick={() => onSelect(t.tabId)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') onSelect(t.tabId);
            }}
            title={t.url}
            className={cx(
              'group flex max-w-56 min-w-28 cursor-pointer items-center gap-1.5 rounded-t-lg border border-b-0 px-3 py-1.5 text-xs',
              viewing
                ? 'border-line bg-canvas font-medium text-ink'
                : 'border-transparent text-ink-muted hover:bg-black/5',
            )}
          >
            <span
              className={cx(
                'size-1.5 shrink-0 rounded-full',
                active ? 'bg-success' : 'bg-transparent',
              )}
              title={active ? '원격 창의 활성 탭' : undefined}
            />
            <span className="truncate">{t.title || shortUrl(t.url)}</span>
            {canClose && (
              <button
                type="button"
                aria-label="탭 닫기"
                onClick={(e) => {
                  e.stopPropagation();
                  onClose(t.tabId);
                }}
                className="ml-auto rounded p-0.5 text-ink-muted opacity-0 hover:bg-black/10 hover:text-ink group-hover:opacity-100 focus:opacity-100"
              >
                <svg viewBox="0 0 20 20" className="size-3" fill="currentColor" aria-hidden>
                  <path d="M6.3 5.3a1 1 0 0 0-1.4 1.4L8.6 10l-3.7 3.3a1 1 0 1 0 1.4 1.4L10 11.4l3.3 3.3a1 1 0 0 0 1.4-1.4L11.4 10l3.3-3.3a1 1 0 0 0-1.4-1.4L10 8.6 6.3 5.3z" />
                </svg>
              </button>
            )}
          </div>
        );
      })}
      {canCreate && (
        <button
          type="button"
          onClick={onCreate}
          className="my-0.5 rounded-md px-2 text-sm text-ink-muted hover:bg-black/5 hover:text-ink"
          title="새 탭"
        >
          +
        </button>
      )}
    </div>
  );
}
